import type { CaptionSeg } from "./prims";
import { pickVisualKind, deriveItems, maybeUpgradeKind, type VisualKind } from "./visuals";
import { findElement, findMolecule } from "./visuals-science";
import { parseEngineConfig } from "./visuals-machines";
import { parseBioConfig } from "./visuals-biology";
import { parseCircuitConfig, CIRCUIT_RE } from "./visuals-circuit";
import { parseGeometryConfig, GEOMETRY_RE } from "./visuals-geometry";
import { parseChartSpec, extractPercent } from "./visuals-charts";
import { parseNeuralConfig } from "./visuals-neural";
import { parseMascotConfig } from "./visuals-mascot";
import { parseRhythmConfig, RHYTHM_RE } from "./visuals-rhythm";

const VISUAL_KINDS: VisualKind[] = ["flow", "network", "cycle", "grow", "compare", "atom", "molecule", "engine", "bio", "circuit", "geometry", "chart", "neural", "pipeline", "archflow", "rhythm", "mascot", "terminal", "image", "sticker", "none"];

/**
 * Anivaze v4 — the Director (deterministic first).
 *
 * Turns a topic or a pasted script into a STRUCTURED, editable storyboard of
 * beats. This rule-based version is the deterministic fallback the whole product
 * leans on (PLAN §11, §21): even with no AI it yields a valid, on-format,
 * reviewable storyboard — never empty, never "random". The LLM Director will
 * later produce the SAME `Beat[]` shape, so everything downstream is unchanged.
 */

export type FormatId = "explainer" | "quote" | "story";
export type BeatRole = "hook" | "body" | "quote" | "recap" | "cta";

export interface VisualPhase {
  kind: VisualKind;
  items: string[];
}

export interface BeatVisual {
  kind: VisualKind;
  /** Short node/stage labels the animated diagram illustrates. */
  items: string[];
  /**
   * Optional CHOREOGRAPHY: 2–3 sub-scenes acted out in order across the beat,
   * mirroring the narration's chronology ("nucleons cluster → electrons orbit →
   * atoms bond"). When present the renderer plays them as a mini-story with a
   * zoom hand-off between phases — an animation, not a slide.
   */
  phases?: VisualPhase[];
  /** Client-side enrichment: data URI of the generated asset (image/sticker kinds). */
  assetUri?: string;
  /** Credit line for a SOURCED image (e.g. "Benh LIEU SONG · CC BY-SA 4.0") — CC attribution. */
  credit?: string;
}

export interface Beat {
  id: string;
  role: BeatRole;
  /** Short on-screen headline (≤ ~6 words, may wrap to 2 lines). */
  title: string;
  /** Full narration for this beat (spoken later; also the caption source). */
  narration: string;
  /** Synced caption lines with timing inside the beat. */
  captions: CaptionSeg[];
  /** Beat template id (see beats.tsx). */
  template: string;
  /** The animated concept visual this beat illustrates itself with. */
  visual: BeatVisual;
  dur: number;
}

export interface Storyboard {
  format: FormatId;
  topic: string;
  beats: Beat[];
}

const WORDS_PER_SEC = 2.6;
const MIN_BEAT = 2.6;
const MAX_BEAT = 9;

const words = (s: string): string[] => s.trim().split(/\s+/).filter(Boolean);

/** Duration a line of narration needs to be spoken, clamped to beat bounds. */
export function narrationDuration(text: string): number {
  const n = words(text).length;
  return Math.max(MIN_BEAT, Math.min(MAX_BEAT, n / WORDS_PER_SEC + 1.1));
}

/** A short on-screen title from a sentence: first meaningful clause, ≤ maxWords. */
export function titleFrom(sentence: string, maxWords = 6): string {
  const clause = sentence.split(/[,:;—-]/)[0] ?? sentence;
  const w = words(clause).filter((x) => !/^(the|a|an|to|of|and|is|are|it|that|this|so|but|for|in|on)$/i.test(x));
  const pick = (w.length >= 2 ? w : words(clause)).slice(0, maxWords);
  const t = pick.join(" ").replace(/[.!?]+$/, "");
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Split narration into 3–6 word caption chunks, evenly timed across the beat. */
export function toCaptions(text: string, dur: number): CaptionSeg[] {
  const w = words(text);
  if (w.length === 0) return [];
  const chunkSize = w.length <= 6 ? w.length : w.length <= 12 ? Math.ceil(w.length / 2) : 5;
  const chunks: string[] = [];
  for (let i = 0; i < w.length; i += chunkSize) chunks.push(w.slice(i, i + chunkSize).join(" "));
  const pad = 0.25;
  const span = Math.max(0.001, dur - pad * 2);
  const totalWords = w.length;
  let acc = 0;
  return chunks.map((text) => {
    const cw = words(text).length;
    const start = pad + (acc / totalWords) * span;
    acc += cw;
    const end = pad + (acc / totalWords) * span;
    return { text, start, end };
  });
}

/** Split raw script text into scene-sized chunks (blank lines, else sentences). */
function splitScenes(text: string): string[] {
  const byBlank = text
    .split(/\n\s*\n/)
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  if (byBlank.length > 1) return byBlank;

  // One block → split into sentences, then group ~2 sentences per scene.
  const sentences = (text.replace(/\s+/g, " ").match(/[^.!?]+[.!?]*/g) ?? [text])
    .map((s) => s.trim())
    .filter(Boolean);
  const scenes: string[] = [];
  for (let i = 0; i < sentences.length; i += 2) {
    scenes.push(sentences.slice(i, i + 2).join(" "));
  }
  return scenes.filter(Boolean);
}

const templateForRole = (role: BeatRole, format: FormatId): string => {
  if (role === "hook") return format === "quote" ? "quote" : "title";
  if (role === "quote") return "quote";
  if (role === "cta") return "cta";
  if (role === "recap") return "title";
  return "point";
};

/** Strip a leading `[Speaker]` tag (crreo-style) — we ignore speakers for now. */
const stripSpeaker = (s: string): string => s.replace(/^\s*\[[^\]]+\]\s*/, "");

/**
 * Manual script → storyboard. Deterministic and total: any non-empty text yields
 * a valid, ordered, on-format storyboard the user can then edit.
 */
export function segmentScript(rawText: string, format: FormatId, topic = ""): Storyboard {
  const text = rawText.trim();
  const scenes = (text ? splitScenes(text) : []).map(stripSpeaker).filter(Boolean);

  // Always guarantee at least a hook + a CTA so the result is a real video.
  if (scenes.length === 0) {
    scenes.push(topic || "Your idea goes here", "Follow for more");
  }

  const n = scenes.length;
  const beats: Beat[] = scenes.map((scene, i) => {
    const role = roleAt(i, n, format);
    return compileBeat(i, role, titleForRole(scene, role, format), scene, format);
  });

  return { format, topic, beats };
}

/** Role for a beat by position + format. */
function roleAt(i: number, n: number, format: FormatId): BeatRole {
  if (i === 0) return "hook";
  if (i === n - 1 && n > 1) return "cta";
  return format === "quote" ? "quote" : "body";
}

function titleForRole(source: string, role: BeatRole, format: FormatId): string {
  if (role === "cta") return titleFrom(source, 4) || "Follow for more";
  return titleFrom(source, format === "quote" ? 8 : 6) || titleFrom(source, 6) || "Untitled";
}

/**
 * Compile one semantic beat (role + title + narration) into a full, renderable
 * Beat — deterministic captions, duration, and template. This is the "compiler"
 * seam: whether the beat came from the rule-based splitter OR the AI Director,
 * timing and layout are computed here identically. See PLAN §3.
 */
const cleanItems = (raw: unknown): string[] =>
  (Array.isArray(raw) ? raw : [])
    .map((s) => String(s).replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .slice(0, 4);

const validKind = (raw: unknown): VisualKind | null =>
  (VISUAL_KINDS as string[]).includes(String(raw)) ? (String(raw) as VisualKind) : null;

/**
 * A generic diagram (flow/compare/cycle/network/grow) is only the RIGHT answer
 * when the beat is genuinely abstract/relational — a process, a comparison, a
 * quantity, a cycle, a relationship. This matches those signals so we KEEP the
 * diagram there, and (via the caller) fall back to a real generated picture for
 * everything else — never an unrelated chart for a concrete thing.
 */
/** First word of a flow item that marks it as an imperative STEP, not a noun. */
const STEP_VERB_RE =
  /^(count|add|subtract|multiply|divide|check|recheck|verify|test|measure|mix|stir|heat|cool|pour|match|balance|cut|place|put|remove|press|turn|open|close|start|stop|wait|repeat|apply|choose|pick|select|write|draw|list|combine|collect|gather|install|run|deploy|build|compile|plan|review|identify|find|solve|simplify|estimate|round)\b/i;

const ABSTRACT_RE =
  /\b(process|procedure|steps?|stages?|phase|sequence|pipeline|workflow|how it works|mechanism|compare|comparison|versus|\bvs\b|difference|trade-?off|percent|%|\brate\b|ratio|proportion|growth|grow|increase|decrease|decline|trend|statistic|\bdata\b|metric|number of|\bcount\b|amount|cycle|loop|repeat|feedback|iterat|network|\bgraph\b|relationship|\bconnect|\blink|hierarchy|timeline|history|evolution|distribution|breakdown|categor|classif|framework|flow of)\b/i;

/** Strip conversational lead-ins + leading articles so the image is grounded in
 * the real subject noun ("teach me about the Roman Colosseum" → "Roman Colosseum"). */
const topicSubject = (topic: string): string =>
  topic.replace(/^\s*(teach me|show me|explain( to me)?|tell me( about)?|what (is|are)|how (do|does|to|can)|why (do|does|is|are))\s+/i, "")
    .replace(/^\s*(the|a|an)\s+/i, "")
    .replace(/[?.!]+$/, "").trim();

/**
 * A self-contained subject for image generation, GROUNDED in the topic so the
 * picture is actually related to the lesson (the reported failure: unrelated
 * charts/images). Leads with the topic subject when the title alone is generic.
 */
const imageSubject = (title: string, topic: string): string => {
  const t = title.replace(/["'']/g, "").replace(/\s*[:\-–—]\s.*$/, "").trim();
  const head = topicSubject(topic);
  if (!head) return t || "illustration";
  const firstWord = head.toLowerCase().split(/\s+/)[0] || "";
  return t.toLowerCase().includes(firstWord) ? t : `${head} — ${t}`;
};

export function compileBeat(
  i: number,
  role: BeatRole,
  title: string,
  narration: string,
  format: FormatId,
  visual?: { kind?: string; items?: string[]; phases?: Array<{ kind?: string; items?: string[] }> },
  topic = "",
): Beat {
  const text = (narration || title || " ").trim() || " ";
  const dur = narrationDuration(text);
  const finalTitle = title || titleFrom(text, 6) || "Untitled";
  const beatText = `${finalTitle} ${narration}`;
  let kind: VisualKind = validKind(visual?.kind) ?? pickVisualKind(role, beatText);
  // A beat that is clearly about a machine must SHOW the machine — even when
  // the AI labelled it a generic diagram ("flow" for a piston beat → engine).
  if (role !== "hook" && role !== "cta") kind = maybeUpgradeKind(kind, beatText);
  let items = (visual?.items && visual.items.length
    ? cleanItems(visual.items)
    : deriveItems(finalTitle, kind)
  ).slice(0, 4);
  // Guard against mislabeled science kinds (atom["Spark"] would wrongly render
  // a default Carbon) — downgrade to a flow chip, where the label still gets a
  // matching icon (Spark → bolt).
  if (kind === "atom" && !findElement(items.join(" "))) kind = "flow";
  if (kind === "molecule" && !findMolecule(items.join(" "))) kind = "flow";
  // Engine items carry mode + focus derived from the FULL beat text, so the
  // actor spotlights the right stage ("transfer port opens" → transfer).
  if (kind === "engine") {
    const cfg = parseEngineConfig(`${items.join(" ")} ${beatText}`);
    items = cfg.focus === "none" ? [cfg.mode] : [cfg.mode, cfg.focus];
  }
  // Bio subject is read from the FULL beat text so "splits into two cells"
  // renders the division actor, not whatever noun the AI put in items.
  if (kind === "bio") {
    items = [parseBioConfig(`${items.join(" ")} ${beatText}`).subject];
  }
  // Guard: the AI sometimes mislabels an electronic thing (RAM, CPU, a chip) as a
  // "circuit" — but a battery/bulb schematic is nonsense for RAM. If the beat has
  // NO real electrical-circuit terms, it's a mispick → show a real picture of the
  // actual thing instead (handled by the image path below).
  if (kind === "circuit" && !CIRCUIT_RE.test(beatText)) {
    kind = "image";
    items = [imageSubject(finalTitle, topic)];
  }
  // Circuit component + on/off state read from the full beat text.
  if (kind === "circuit") {
    const c = parseCircuitConfig(`${items.join(" ")} ${beatText}`);
    items = [c.component, c.state];
  }
  // Geometry subject: the TITLE is the cleanest signal ("Lines Extend…" → line),
  // so prefer it; fall back to the narration only when the title names no figure.
  if (kind === "geometry") {
    const src = GEOMETRY_RE.test(finalTitle) ? finalTitle : beatText;
    items = [parseGeometryConfig(src).subject];
  }
  // Neural subject reads title + narration together (a "The Network Learns by
  // Failing" title is really backprop once the narration is considered).
  if (kind === "neural") {
    items = [parseNeuralConfig(`${finalTitle} ${beatText}`).subject];
  }
  // Mascot: pose reacts to the narration; the speech bubble carries the title.
  if (kind === "mascot") {
    const pose = parseMascotConfig(`${finalTitle} ${beatText}`).pose;
    items = [pose, ...finalTitle.split(/\s+/).filter(Boolean).slice(0, 8)];
  }
  // Music / rhythm / sound beats VISUALIZE the phenomenon — beat pulse, waveform,
  // notation, metronome — NOT a repeated instrument image or a bare rail (the
  // reported "same instrument printed again and again" defect). Only rail-ish /
  // image kinds are redirected; a specific instrument beat has no rhythm words and
  // stays an image.
  if (RHYTHM_RE.test(beatText) && role !== "hook" && role !== "cta") {
    const railish = kind === "flow" || kind === "network" || kind === "compare" ||
      kind === "grow" || kind === "cycle" || kind === "image" || kind === "sticker" || kind === "none";
    if (railish) kind = "rhythm";
  }
  if (kind === "rhythm") {
    const rc = parseRhythmConfig(`${finalTitle} ${beatText}`);
    const given = (items[0] ?? "").trim().toLowerCase();
    // Honor a valid subject the planner picked; otherwise derive from the text.
    const subject = ["pulse", "wave", "staff", "metronome"].includes(given) ? given : rc.subject;
    items = [subject, `${rc.bpm}bpm`];
  }
  // A "chart" from the AI must carry valid data (honesty rule) — else it's just
  // a quantitative intent, so downgrade to the abstract grow trend.
  if (kind === "chart" && !parseChartSpec(items)) kind = "grow";
  // A grow/compare beat whose narration states a REAL percentage becomes a
  // donut chart of that number — a chart of actual data beats an abstract trend.
  if (kind === "grow" || kind === "compare") {
    const pct = extractPercent(beatText);
    if (pct !== null) {
      kind = "chart";
      items = ["donut", String(pct), ""];
    }
  }
  // ── PNG FALLBACK ──────────────────────────────────────────────────────────
  // No dedicated actor matched. If this body/recap beat is NOT genuinely
  // abstract/relational, its subject is a concrete THING — so show a REAL
  // generated picture of it (tagged + reused ≥5× across videos), grounded in the
  // topic. This is the fix for "unrelated charts instead of a related image":
  // we never leave a concrete subject on a chart/rail hoping it fits.
  let imaged = false;
  // Kinds that misfire for concrete subjects — a chart of amounts (compare/grow)
  // or a node-rail (network) — always fall through to a real picture. A `flow`
  // falls through ONLY when its items are nouns-on-a-rail: items that read as
  // imperative STEPS ("Count atoms", "Add coefficients") are a genuine procedure
  // the Teacher board writes out, and planned phase choreography is kept too.
  const chartOrRail = kind === "network" || kind === "compare" || kind === "grow";
  const looksLikeSteps = items.length >= 2 &&
    items.filter((it) => STEP_VERB_RE.test(it.trim())).length >= Math.ceil(items.length / 2);
  const hasChoreo = Array.isArray(visual?.phases) && visual!.phases!.length >= 2;
  const nounRail = kind === "flow" && !looksLikeSteps && !hasChoreo;
  if ((chartOrRail || nounRail) && role !== "hook" && role !== "cta" && format !== "quote" && !ABSTRACT_RE.test(beatText)) {
    kind = "image";
    items = [imageSubject(finalTitle, topic)];
    imaged = true;
  }
  // VARIETY: a genuine step-flow that SURVIVED (a real process, not a noun-rail)
  // renders as the flat rail on some beats and the 3D isometric pipeline on
  // others, so consecutive process beats don't all look identical. Deterministic
  // on beat index → preview === export preserved.
  if (kind === "flow" && i % 2 === 1 && items.length >= 2 && items.length <= 4) {
    kind = "pipeline";
  }
  // Choreography: keep only valid, non-empty phases; need ≥2 to be a story.
  // (An image beat plays no phases — it IS the picture.)
  const phases: VisualPhase[] | undefined = (() => {
    if (imaged || !Array.isArray(visual?.phases)) return undefined;
    const ps = visual!.phases!
      .map((p) => ({ kind: validKind(p?.kind), items: cleanItems(p?.items) }))
      .filter((p): p is VisualPhase => p.kind !== null && p.kind !== "none" && p.items.length > 0)
      .slice(0, 3);
    return ps.length >= 2 ? ps : undefined;
  })();
  return {
    id: `beat-${i}`,
    role,
    title: finalTitle,
    narration: narration || title || "",
    captions: toCaptions(text, dur),
    template: templateForRole(role, format),
    visual: { kind, items, ...(phases ? { phases } : {}) },
    dur,
  };
}

/** Semantic beat as produced by the AI Director (content only; no timing/layout). */
export interface SemanticBeat {
  role?: string;
  title?: string;
  narration?: string;
  visual?: { kind?: string; items?: string[]; phases?: Array<{ kind?: string; items?: string[] }> };
}

const ROLES: BeatRole[] = ["hook", "body", "quote", "recap", "cta"];
const normalizeRole = (r: unknown): BeatRole | null => {
  const s = String(r ?? "").toLowerCase().trim();
  return (ROLES as string[]).includes(s) ? (s as BeatRole) : null;
};

/**
 * AI Director output (semantic beats) → a full, valid, on-format Storyboard.
 * Total and always-valid (guarantees hook-first / CTA-last, no empty beats); if
 * the AI returns nothing usable, degrades to the deterministic splitter on the
 * topic. Same guarantees as `segmentScript` (PLAN §21).
 */
export function buildStoryboard(semantic: SemanticBeat[], format: FormatId, topic = ""): Storyboard {
  const clean = (semantic ?? [])
    .map((b) => ({
      role: normalizeRole(b.role),
      title: (b.title ?? "").toString().replace(/\s+/g, " ").trim(),
      narration: (b.narration ?? "").toString().replace(/\s+/g, " ").trim(),
      visual: b.visual,
    }))
    .filter((b) => b.title || b.narration);

  if (clean.length === 0) return segmentScript(topic, format, topic);

  const n = clean.length;
  const beats = clean.map((b, i) => {
    const role: BeatRole = i === 0 ? "hook" : i === n - 1 && n > 1 ? "cta" : b.role ?? roleAt(i, n, format);
    const narration = b.narration || b.title;
    const title = b.title || titleForRole(b.narration, role, format);
    return compileBeat(i, role, title, narration, format, b.visual, topic);
  });

  return { format, topic, beats: avoidAdjacentRepeats(beats) };
}

/**
 * Anti-repetition (PLATFORM-ADDITIONS §1): don't show the SAME diagram kind on
 * back-to-back beats when a same-content sibling exists — it breaks up a monotone
 * run of identical visuals (the "again and again" complaint). Only the visual
 * STYLE changes; the items/content are preserved. Image/actor beats are left
 * alone (their content is bound to the narration; server-side grow-and-mix varies
 * the actual asset).
 */
const DIAGRAM_SIBLING: Partial<Record<VisualKind, VisualKind>> = { flow: "pipeline", pipeline: "flow", network: "flow" };
export function avoidAdjacentRepeats(beats: Beat[]): Beat[] {
  let prev: VisualKind | null = null;
  return beats.map((b) => {
    const k = b.visual.kind;
    const sib = DIAGRAM_SIBLING[k];
    if (k === prev && sib) {
      const okItems = sib !== "pipeline" || (b.visual.items.length >= 2 && b.visual.items.length <= 4);
      if (okItems) {
        prev = sib;
        return { ...b, visual: { ...b.visual, kind: sib } };
      }
    }
    prev = k;
    return b;
  });
}

/** Total storyboard duration (seconds). */
export const storyboardDuration = (sb: Storyboard): number =>
  sb.beats.reduce((s, b) => s + b.dur, 0);

/** Which beat is active at global time `t`, plus its local time. */
export function beatAtTime(sb: Storyboard, t: number): { beat: Beat; index: number; localTime: number } {
  let acc = 0;
  for (let i = 0; i < sb.beats.length; i++) {
    const b = sb.beats[i];
    if (t < acc + b.dur || i === sb.beats.length - 1) {
      return { beat: b, index: i, localTime: Math.max(0, t - acc) };
    }
    acc += b.dur;
  }
  return { beat: sb.beats[0], index: 0, localTime: 0 };
}
